/**
 * Service Service
 * Functions for fetching and managing services from Supabase
 */

import { supabase, isSupabaseConfigured } from "./supabase";
import { Service, ServiceCategory } from "@/types/database.types";

/**
 * Extended Service type with UI-specific fields
 * These fields are stored in the form_schema or computed
 */
export interface ServiceWithMeta extends Service {
  processing_time?: string;
  fee?: string;
  requirements?: string[];
  steps?: { title: string; description: string }[];
  faqs?: { question: string; answer: string }[];
  documents?: string[];
  long_description?: string;
  popular?: boolean;
}

/**
 * Service metadata stored in the form_schema JSON
 */
interface ServiceMetadata {
  processing_time?: string;
  fee?: string;
  requirements?: string[];
  steps?: { title: string; description: string }[];
  faqs?: { question: string; answer: string }[];
  documents?: string[];
  long_description?: string;
  popular?: boolean;
}

/**
 * Fetch all active services from the database
 * @param category - Optional category filter
 * @returns Array of services with metadata
 */
export async function getServices(category?: ServiceCategory): Promise<{
  data: ServiceWithMeta[] | null;
  error: Error | null;
}> {
  if (!isSupabaseConfigured()) {
    return {
      data: null,
      error: new Error(
        "Supabase is not configured. Please set environment variables.",
      ),
    };
  }

  try {
    let query = supabase
      .from("es_services")
      .select("*")
      .eq("is_active", true)
      .eq("status", "published")
      .order("name", { ascending: true });

    if (category && category !== "General") {
      query = query.eq("category", category);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Parse metadata from form_schema for each service
    const servicesWithMeta: ServiceWithMeta[] = (data || []).map((service) => {
      const metadata = extractMetadata(service.form_schema);
      return {
        ...service,
        ...metadata,
      };
    });

    return { data: servicesWithMeta, error: null };
  } catch (err) {
    console.error("Error fetching services:", err);
    return {
      data: null,
      error: err instanceof Error ? err : new Error("Failed to fetch services"),
    };
  }
}

/**
 * Fetch a single service by its slug
 * @param slug - The URL-friendly service identifier
 * @returns Service with metadata or null if not found
 */
export async function getServiceBySlug(slug: string): Promise<{
  data: ServiceWithMeta | null;
  error: Error | null;
}> {
  if (!isSupabaseConfigured()) {
    return {
      data: null,
      error: new Error(
        "Supabase is not configured. Please set environment variables.",
      ),
    };
  }

  try {
    const { data, error } = await supabase
      .from("es_services")
      .select("*")
      .eq("slug", slug)
      .eq("is_active", true)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return { data: null, error: new Error("Service not found") };
    }

    // Parse metadata from form_schema
    const metadata = extractMetadata(data.form_schema);
    const serviceWithMeta: ServiceWithMeta = {
      ...data,
      ...metadata,
    };

    return { data: serviceWithMeta, error: null };
  } catch (err) {
    console.error("Error fetching service:", err);
    return {
      data: null,
      error: err instanceof Error ? err : new Error("Failed to fetch service"),
    };
  }
}

/**
 * Search services by name or description
 * @param query - Search query string
 * @returns Array of matching services
 */
export async function searchServices(query: string): Promise<{
  data: ServiceWithMeta[] | null;
  error: Error | null;
}> {
  if (!isSupabaseConfigured()) {
    return {
      data: null,
      error: new Error(
        "Supabase is not configured. Please set environment variables.",
      ),
    };
  }

  if (!query.trim()) {
    return getServices();
  }

  try {
    const { data, error } = await supabase
      .from("es_services")
      .select("*")
      .eq("is_active", true)
      .eq("status", "published")
      .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
      .order("name", { ascending: true });

    if (error) {
      throw error;
    }

    // Parse metadata from form_schema for each service
    const servicesWithMeta: ServiceWithMeta[] = (data || []).map((service) => {
      const metadata = extractMetadata(service.form_schema);
      return {
        ...service,
        ...metadata,
      };
    });

    return { data: servicesWithMeta, error: null };
  } catch (err) {
    console.error("Error searching services:", err);
    return {
      data: null,
      error:
        err instanceof Error ? err : new Error("Failed to search services"),
    };
  }
}

/**
 * Extract UI metadata from the form_schema JSON field
 * The form_schema stores both form definition and UI metadata
 */
function extractMetadata(formSchema: unknown): ServiceMetadata {
  if (!formSchema || typeof formSchema !== "object") {
    return getDefaultMetadata();
  }

  const schema = formSchema as { metadata?: Record<string, unknown> } & Record<
    string,
    unknown
  >;
  const metadata = schema.metadata || schema;

  return {
    processing_time:
      (metadata.processing_time as string) ||
      getDefaultMetadata().processing_time,
    fee: (metadata.fee as string) || getDefaultMetadata().fee,
    requirements:
      (metadata.requirements as string[]) || getDefaultMetadata().requirements,
    steps:
      (metadata.steps as { title: string; description: string }[]) ||
      getDefaultMetadata().steps,
    faqs:
      (metadata.faqs as { question: string; answer: string }[]) ||
      getDefaultMetadata().faqs,
    documents:
      (metadata.documents as string[]) || getDefaultMetadata().documents,
    long_description: (metadata.long_description as string) || undefined,
    popular: (metadata.popular as boolean) || false,
  };
}

/**
 * Get default metadata values for services without complete metadata
 */
function getDefaultMetadata(): ServiceMetadata {
  return {
    processing_time: "5-10 business days",
    fee: "Free",
    requirements: [],
    steps: [
      {
        title: "Submit Application",
        description: "Complete the online form with required information.",
      },
      {
        title: "Review",
        description: "Your application will be reviewed by our team.",
      },
      {
        title: "Decision",
        description: "You will receive a notification with the final decision.",
      },
    ],
    faqs: [],
    documents: [],
    popular: false,
  };
}

/**
 * Get icon name based on service category
 * Maps service categories to icon identifiers
 */
export function getServiceIconName(category: ServiceCategory): string {
  const iconMap: Record<ServiceCategory, string> = {
    Admissions: "GraduationCap",
    Transfers: "Building",
    Examinations: "ClipboardList",
    Certifications: "Award",
    General: "FileText",
  };
  return iconMap[category] || "FileText";
}

/**
 * Get all unique service categories
 */
export async function getServiceCategories(): Promise<{
  data: ServiceCategory[] | null;
  error: Error | null;
}> {
  if (!isSupabaseConfigured()) {
    return {
      data: null,
      error: new Error(
        "Supabase is not configured. Please set environment variables.",
      ),
    };
  }

  try {
    const { data, error } = await supabase
      .from("es_services")
      .select("category")
      .eq("is_active", true);

    if (error) {
      throw error;
    }

    // Extract unique categories
    const categories = [
      ...new Set(data?.map((s) => s.category)),
    ] as ServiceCategory[];

    return { data: categories, error: null };
  } catch (err) {
    console.error("Error fetching categories:", err);
    return {
      data: null,
      error:
        err instanceof Error ? err : new Error("Failed to fetch categories"),
    };
  }
}
